import { useState } from 'react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';

const INITIAL_EDIT_FORM = {
  minimum_order_amount: '',
  payment_terms: '',
  preferred_delivery_time: '',
  allowed_delivery_days: [],
  credit_limit: '',
  invoice_email: '',
  auto_invoice: true,
};

// Owns all state + handlers for the client detail dialog (overview/terms/locations/
// notes/history/documents). `onClientsChanged` refreshes the parent client list.
export const useClientDetail = ({ onClientsChanged }) => {
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [savingChanges, setSavingChanges] = useState(false);
  const [auditLog, setAuditLog] = useState({ entries: [], total: 0, page: 1, total_pages: 1 });
  const [auditPage, setAuditPage] = useState(1);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [clientDocs, setClientDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [editForm, setEditForm] = useState(INITIAL_EDIT_FORM);

  const loadClientDetail = async (clientId) => {
    setLoadingDetail(true);
    try {
      const data = await adminService.getClient(clientId);
      setClientDetail(data);
      setEditForm({
        minimum_order_amount: data.preferences?.minimum_order_amount ?? 50,
        payment_terms: data.preferences?.payment_terms || 'Net 30',
        preferred_delivery_time: data.preferences?.preferred_delivery_time || '',
        allowed_delivery_days: data.preferences?.allowed_delivery_days || [],
        credit_limit: data.preferences?.credit_limit || '',
        invoice_email: data.preferences?.invoice_email || '',
        auto_invoice: data.preferences?.auto_invoice !== false,
        auto_pay_enabled: data.preferences?.auto_pay_enabled || false,
      });
    } catch (error) {
      console.error('Error loading client detail:', error);
      toast.error('Failed to load client details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const loadAuditLog = async (clientId, page = 1) => {
    setLoadingAudit(true);
    try {
      const data = await adminService.getClientAuditLog(clientId, page);
      setAuditLog(data);
      setAuditPage(page);
    } catch (error) {
      console.error('Error loading audit log:', error);
    } finally {
      setLoadingAudit(false);
    }
  };

  const loadClientDocs = async (clientId) => {
    setLoadingDocs(true);
    try {
      const data = await adminService.getClientDocuments(clientId);
      setClientDocs(data);
    } catch (error) {
      console.error('Error loading client documents:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const openClient = (client) => {
    setSelectedClient(client);
    setDetailOpen(true);
    setEditMode(false);
    loadClientDetail(client.id);
    loadAuditLog(client.id, 1);
    loadClientDocs(client.id);
  };

  const handleSavePreferences = async () => {
    setSavingChanges(true);
    try {
      const cleanData = {};
      for (const [key, value] of Object.entries(editForm)) {
        if (value === '' || value === undefined) continue;
        if (key === 'minimum_order_amount' || key === 'credit_limit') {
          const num = parseFloat(value);
          if (!isNaN(num)) cleanData[key] = num;
        } else {
          cleanData[key] = value;
        }
      }
      await adminService.updateClientPreferences(clientDetail.id, cleanData);
      toast.success('Client preferences updated');
      setEditMode(false);
      loadClientDetail(clientDetail.id);
      loadAuditLog(clientDetail.id, 1);
      if (onClientsChanged) onClientsChanged();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save changes');
    } finally {
      setSavingChanges(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await adminService.addClientNote(clientDetail.id, newNote);
      toast.success('Note added');
      setNewNote('');
      loadClientDetail(clientDetail.id);
      loadAuditLog(clientDetail.id, 1);
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await adminService.deleteClientNote(clientDetail.id, noteId);
      toast.success('Note deleted');
      loadClientDetail(clientDetail.id);
      loadAuditLog(clientDetail.id, 1);
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  const handleSaveLocation = async (location) => {
    try {
      if (location.id) {
        await adminService.updateClientLocation(clientDetail.id, location.id, location);
        toast.success('Location updated');
      } else {
        await adminService.addClientLocation(clientDetail.id, location);
        toast.success('Location added');
      }
      setEditingLocation(null);
      loadClientDetail(clientDetail.id);
      loadAuditLog(clientDetail.id, 1);
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error('Failed to save location');
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    try {
      await adminService.deleteClientLocation(clientDetail.id, locationId);
      toast.success('Location deleted');
      loadClientDetail(clientDetail.id);
      loadAuditLog(clientDetail.id, 1);
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error('Failed to delete location');
    }
  };

  return {
    detailOpen,
    setDetailOpen,
    selectedClient,
    clientDetail,
    loadingDetail,
    editMode,
    setEditMode,
    editForm,
    setEditForm,
    savingChanges,
    newNote,
    setNewNote,
    editingLocation,
    setEditingLocation,
    auditLog,
    auditPage,
    loadingAudit,
    clientDocs,
    loadingDocs,
    openClient,
    loadAuditLog,
    handleSavePreferences,
    handleAddNote,
    handleDeleteNote,
    handleSaveLocation,
    handleDeleteLocation,
  };
};
